import { useState } from 'react';
import clsx from 'clsx';
import { calculateOpslagkosten } from '@platform/opslagkosten';
import type { RouteOption } from '../../lib/appointments';

interface Props {
  routes: RouteOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  showExtra: boolean;
  onToggleExtra: () => void;
  isLoading: boolean;
  error: string | null;
  onBumpCapacity?: (routeId: string) => void;
  bypassActive?: boolean;
  onEnableBypass?: () => void;
  /** Bypass-mode filter: toon alleen routes van deze master-route. null = alle. */
  bypassRouteNaamFilter?: string | null;
  onChangeBypassRouteNaamFilter?: (naam: string | null) => void;
  /** Startdatum voor opslag-berekening (Datum_Tijd_Aflevervoorstel_verstuurd__c). */
  opslagkostenStartDatum?: string | null;
  /** Toon opslag-badges per route. Off bij pickup of als waiver is aangevinkt. */
  showOpslag?: boolean;
}

const INITIAL_COUNT = 3;

export function RouteOptionList({
  routes,
  selectedId,
  onSelect,
  showExtra,
  onToggleExtra,
  isLoading,
  error,
  onBumpCapacity,
  bypassActive = false,
  onEnableBypass,
  bypassRouteNaamFilter = null,
  onChangeBypassRouteNaamFilter,
  opslagkostenStartDatum = null,
  showOpslag = false,
}: Props) {
  // Venster-dagen (gebied tijdelijk dicht) staan standaard niet in de lijst.
  // Een badge bleek te makkelijk over het hoofd te zien; wie er tóch een nodig
  // heeft klapt ze bewust open.
  const [toonGesloten, setToonGesloten] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse" role="status" aria-label="Routes laden">
        <div className="h-12 bg-slate-100 rounded-md" />
        <div className="h-12 bg-slate-100 rounded-md" />
        <div className="h-12 bg-slate-100 rounded-md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
        {error}
      </div>
    );
  }

  // In bypass-mode: opt. filter op master-route. Buiten bypass: filter is no-op.
  const filteredRoutes = bypassActive && bypassRouteNaamFilter
    ? routes.filter((r) => r.routeNaam === bypassRouteNaamFilter)
    : routes;

  // Gebied dicht op die datum: uit de standaardlijst houden. De reden komt uit
  // het eerste gesloten record, want het venster staat op het gebied en geldt
  // dus voor alle dagen erin.
  const geslotenRoutes = filteredRoutes.filter((r) => r.gebiedGesloten === true);
  const geslotenReden = geslotenRoutes.find((r) => r.gebiedGeslotenReden)?.gebiedGeslotenReden ?? null;
  const bruikbareRoutes = toonGesloten
    ? filteredRoutes
    : filteredRoutes.filter((r) => r.gebiedGesloten !== true);

  const visible = showExtra ? bruikbareRoutes : bruikbareRoutes.slice(0, INITIAL_COUNT);
  const hiddenCount = Math.max(0, bruikbareRoutes.length - INITIAL_COUNT);

  // Distinct routeNamen uit de bypass-resultaten — altijd uit de volledige set
  // zodat kiezen tussen filters werkt.
  const beschikbareRouteNamen = bypassActive
    ? Array.from(new Set(routes.map((r) => r.routeNaam).filter(Boolean) as string[])).sort()
    : [];

  return (
    <div className="space-y-2">
      {bypassActive && beschikbareRouteNamen.length > 1 && onChangeBypassRouteNaamFilter && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-md">
          <label className="text-xs font-medium text-amber-900 whitespace-nowrap">
            Master-route:
          </label>
          <select
            value={bypassRouteNaamFilter ?? ''}
            onChange={(e) => onChangeBypassRouteNaamFilter(e.target.value || null)}
            className="flex-1 text-xs px-2 py-1 border border-amber-300 rounded bg-white text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <option value="">Alle ({routes.length})</option>
            {beschikbareRouteNamen.map((naam) => {
              const count = routes.filter((r) => r.routeNaam === naam).length;
              return (
                <option key={naam} value={naam}>
                  Route {naam} ({count})
                </option>
              );
            })}
          </select>
        </div>
      )}

      {geslotenRoutes.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
          <span className="text-sm leading-5">⛔</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-red-900">
              Gebied dicht{geslotenReden ? `: ${geslotenReden}` : ''}
            </div>
            <div className="text-[11px] text-red-800">
              {geslotenRoutes.length} moment{geslotenRoutes.length === 1 ? '' : 'en'} in deze periode
              {toonGesloten ? ' staan hieronder tussen de opties.' : ' zijn verborgen.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setToonGesloten((v) => !v)}
            className="flex-shrink-0 text-[11px] font-medium px-2 py-1 border border-red-300 rounded bg-white text-red-800 hover:bg-red-100"
          >
            {toonGesloten ? 'Verbergen' : 'Toon toch'}
          </button>
        </div>
      )}

      {bruikbareRoutes.length === 0 ? (
        <div className="px-3 py-4 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">
          {bypassActive && bypassRouteNaamFilter
            ? `Geen routes van "${bypassRouteNaamFilter}" beschikbaar.`
            : 'Geen beschikbare momenten gevonden.'}
        </div>
      ) : (
        visible.map((route) => {
          const isSelected = selectedId === route.routeId;
          const isVandaag = route.isVandaag === true;
          const isFull = route.isFull === true;
          const isNietVoorstellen = route.isNietVoorstellen === true;
          const isGebiedGesloten = route.gebiedGesloten === true;
          const isLowCapacity = !isFull && route.capaciteit > 0 && route.capaciteit <= 3;
          const opslag = showOpslag && opslagkostenStartDatum && route.datumISO
            ? calculateOpslagkosten(opslagkostenStartDatum, route.datumISO)
            : null;
          const showOpslagBadge = opslag !== null && !opslag.isGratis;
          return (
            <label
              key={route.routeId}
              className={clsx(
                'flex items-start gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors',
                isSelected
                  ? 'border-indigo-500 bg-indigo-50'
                  : isVandaag
                    ? 'border-amber-300 bg-amber-50/60 hover:border-amber-400'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
              )}
            >
              <input
                type="radio"
                name="route-option"
                className="mt-1 accent-indigo-600"
                checked={isSelected}
                onChange={() => onSelect(route.routeId)}
              />
              <div className="flex-1 min-w-0">
                {bypassActive && route.routeNaam && (
                  <div className="text-[11px] font-medium text-amber-700">
                    Route {route.routeNaam}
                  </div>
                )}
                <div className="text-sm font-medium text-slate-900">
                  {route.dagNaam
                    ? `${route.dagNaam} ${route.dagNummer ?? ''} ${route.maandNaam ?? ''}`.trim()
                    : route.datum}
                </div>
                <div className="text-xs text-slate-500">{route.tijdslot}</div>
                {(isVandaag || isNietVoorstellen || isGebiedGesloten || bypassActive) && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {isVandaag && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        🚐 Chauffeur onderweg
                      </span>
                    )}
                    {isGebiedGesloten && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">
                        ⛔ Gebied dicht{route.gebiedGeslotenReden ? `: ${route.gebiedGeslotenReden}` : ''}
                      </span>
                    )}
                    {isNietVoorstellen && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                        Niet voorstellen
                      </span>
                    )}
                    {bypassActive && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        Buiten dienstgebied
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {route.routeId && (
                  <a
                    href={`/route-planner?routeId=${route.routeId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-slate-400 hover:text-indigo-600 transition-colors"
                    title="Bekijk route in nieuw tab"
                    aria-label="Bekijk route in nieuw tab"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
                {showOpslagBadge && opslag && (
                  <span
                    className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                    title={`${opslag.weken} ${opslag.weken === 1 ? 'week' : 'weken'} opslag à €10/week`}
                  >
                    +€{opslag.bedrag} opslag
                  </span>
                )}
                {isFull ? (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                    Vol
                  </span>
                ) : (
                  <span
                    className={clsx(
                      'text-[10px] font-medium px-1.5 py-0.5 rounded',
                      isLowCapacity
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-50 text-emerald-700'
                    )}
                  >
                    {route.capaciteit} plek{route.capaciteit === 1 ? '' : 'ken'}
                  </span>
                )}
                {onBumpCapacity && (isFull || isLowCapacity) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onBumpCapacity(route.routeId);
                    }}
                    className={clsx(
                      'text-[10px] hover:underline',
                      isFull ? 'text-amber-700 font-medium' : 'text-indigo-600'
                    )}
                  >
                    Capaciteit +
                  </button>
                )}
              </div>
            </label>
          );
        })
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={onToggleExtra}
          className="text-xs text-indigo-600 hover:underline"
        >
          {showExtra ? 'Minder opties tonen' : `Nog ${hiddenCount} opties tonen`}
        </button>
      )}

      {!bypassActive && onEnableBypass && (
        <button
          type="button"
          onClick={onEnableBypass}
          className="text-xs text-amber-700 hover:underline block"
        >
          Andere route kiezen (buiten dienstgebied)
        </button>
      )}
    </div>
  );
}
